/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { I18n } from '@coze-arch/i18n';
import { Toast } from '@coze-arch/coze-design';
import { isApiError } from '@coze-arch/bot-http';

import {
  createOpenCodeSession,
  deleteOpenCodeSession,
  getOpenCodeSessionEmbed,
  listOpenCodeSessions,
  type OpenCodeSessionItem,
  updateOpenCodeSession,
} from '../../api/opencode-sessions';

type FrameTarget = {
  studioID: number;
  opencodeSessionID: string;
  embedUrl: string;
};

const DEFAULT_SESSION_TITLE = '新会话';

export function useCodingSessionsPage() {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [updatingID, setUpdatingID] = useState<number | null>(null);
  const [deletingID, setDeletingID] = useState<number | null>(null);
  const [items, setItems] = useState<OpenCodeSessionItem[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [frameTarget, setFrameTarget] = useState<FrameTarget | null>(null);
  const loadRequestIdRef = useRef(0);
  const openRequestIdRef = useRef(0);
  const openingStudioIDRef = useRef<number>();
  const frameTargetRef = useRef<FrameTarget | null>(null);
  const confirmedFrameTargetRef = useRef<FrameTarget | null>(null);
  const renameInFlightRef = useRef(new Set<number>());

  const updateFrameTarget = useCallback((target: FrameTarget | null) => {
    frameTargetRef.current = target;
    setFrameTarget(target);
  }, []);

  const load = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    try {
      const sessions = await listOpenCodeSessions();
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      setItems(sessions);
    } catch (error) {
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      const msg = isApiError(error)
        ? error.message
        : I18n.t('abr_coding_sessions_load_failed');
      Toast.error(msg);
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openSession = useCallback(async (id: number) => {
    if (frameTargetRef.current?.studioID === id) {
      return;
    }
    const requestId = ++openRequestIdRef.current;
    openingStudioIDRef.current = id;
    try {
      const url = await getOpenCodeSessionEmbed(id);
      if (requestId !== openRequestIdRef.current) {
        return;
      }
      if (!url) {
        Toast.error(I18n.t('abr_coding_sessions_embed_failed'));
        return;
      }
      const item = items.find(session => session.id === id);
      if (!item) {
        return;
      }
      setActiveId(id);
      updateFrameTarget({
        studioID: id,
        opencodeSessionID: item.opencode_session_id,
        embedUrl: url,
      });
    } catch (error) {
      if (requestId !== openRequestIdRef.current) {
        return;
      }
      const msg = isApiError(error)
        ? error.message
        : I18n.t('abr_coding_sessions_embed_failed');
      Toast.error(msg);
    } finally {
      if (requestId === openRequestIdRef.current) {
        openingStudioIDRef.current = undefined;
      }
    }
  }, [items, updateFrameTarget]);

  const handleCreate = useCallback(async () => {
    setCreating(true);
    try {
      const { item, embed_url } = await createOpenCodeSession();
      openRequestIdRef.current += 1;
      openingStudioIDRef.current = undefined;
      setItems(prev => [item, ...prev]);
      setActiveId(item.id);
      updateFrameTarget({
        studioID: item.id,
        opencodeSessionID: item.opencode_session_id,
        embedUrl: embed_url,
      });
    } catch (error) {
      const msg = isApiError(error)
        ? error.message
        : I18n.t('abr_coding_sessions_create_failed');
      Toast.error(msg);
    } finally {
      setCreating(false);
    }
  }, [updateFrameTarget]);

  const handleDelete = useCallback(
    async (id: number) => {
      setDeletingID(id);
      try {
        await deleteOpenCodeSession(id);
        setItems(prev => prev.filter(item => item.id !== id));
        if (openingStudioIDRef.current === id) {
          openRequestIdRef.current += 1;
          openingStudioIDRef.current = undefined;
        }
        if (confirmedFrameTargetRef.current?.studioID === id) {
          confirmedFrameTargetRef.current = null;
        }
        if (frameTargetRef.current?.studioID === id) {
          openRequestIdRef.current += 1;
          setActiveId(null);
          updateFrameTarget(null);
        }
      } catch (error) {
        const msg = isApiError(error)
          ? error.message
          : I18n.t('abr_coding_sessions_delete_failed');
        Toast.error(msg);
      } finally {
        setDeletingID(current => (current === id ? null : current));
      }
    },
    [updateFrameTarget],
  );

  const handleFrameReady = useCallback((sessionID: string) => {
    const target = frameTargetRef.current;
    if (!target || target.opencodeSessionID !== sessionID) {
      return;
    }
    confirmedFrameTargetRef.current = target;
    setActiveId(target.studioID);
  }, []);

  const handleFrameError = useCallback(
    (sessionID: string) => {
      const target = frameTargetRef.current;
      if (!target || target.opencodeSessionID !== sessionID) {
        return;
      }
      Toast.error(I18n.t('abr_coding_sessions_embed_failed'));
      const fallback = confirmedFrameTargetRef.current;
      setActiveId(fallback?.studioID ?? null);
      updateFrameTarget(fallback);
    },
    [updateFrameTarget],
  );

  const updateTitle = useCallback(
    async (id: number, title: string, showError: boolean) => {
      if (renameInFlightRef.current.has(id)) {
        return false;
      }
      renameInFlightRef.current.add(id);
      setUpdatingID(id);
      try {
        const item = await updateOpenCodeSession(id, title);
        setItems(prev =>
          prev.map(currentItem => (currentItem.id === id ? item : currentItem)),
        );
        return true;
      } catch (error) {
        if (showError) {
          const msg = isApiError(error)
            ? error.message
            : I18n.t('abr_coding_sessions_update_failed');
          Toast.error(msg);
        }
        return false;
      } finally {
        renameInFlightRef.current.delete(id);
        setUpdatingID(current => (current === id ? null : current));
      }
    },
    [],
  );

  const handleRename = useCallback(
    async (id: number, nextTitle: string) => {
      const title = nextTitle.trim();
      const current = items.find(item => item.id === id);
      if (!current || !title || title === current.title) {
        return false;
      }
      return updateTitle(id, title, true);
    },
    [items, updateTitle],
  );

  const handleTitleSuggested = useCallback(
    async (opencodeSessionID: string, nextTitle: string) => {
      const title = nextTitle.trim();
      const current = items.find(
        item => item.opencode_session_id === opencodeSessionID,
      );
      if (
        !current ||
        current.title !== DEFAULT_SESSION_TITLE ||
        !title ||
        title === current.title
      ) {
        return;
      }
      await updateTitle(current.id, title, false);
    },
    [items, updateTitle],
  );

  return {
    loading,
    creating,
    updatingID,
    deletingID,
    items,
    activeId,
    frameTarget,
    load,
    openSession,
    handleCreate,
    handleDelete,
    handleRename,
    handleTitleSuggested,
    handleFrameReady,
    handleFrameError,
  };
}
